import useCustomers from '@/api/accounts-receivable/customers/useCustomers';
import useCustomer from '@/api/accounts-receivable/customers/useCustomer';
import { Combobox } from '@/components/ui/combobox';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { useState } from 'react';
import { T, useGT } from 'gt-react';
import CustomerPill from 'src/components/customer/CustomerPill';

/** Customers fetched per batch; the list grows a batch at a time. */
const CUSTOMER_BATCH_SIZE = 20;
type CustomerOption = NonNullable<
  ReturnType<typeof useCustomers>['data']
>['edges'][number];
type SelectedCustomer = NonNullable<
  ReturnType<typeof useCustomer>['data']
>['customer'];

/**
 * The selected customer, fetched on its own, in the list's shape: it may not
 * be in the loaded page (search, paging), but the trigger needs its name.
 */
function toCustomerOption(selected: SelectedCustomer): CustomerOption {
  return {
    id: selected.id,
    name: selected.name,
    erpDisplayName: selected.erpDisplayName,
    aliases: selected.aliases,
    address: selected.address,
    phoneNumber: selected.phoneNumber,
    emailDomain: selected.emailDomain,
    contactPerson: selected.contactPerson,
    paymentTerms: selected.paymentTerms,
    creditLimit: selected.creditLimit,
    creditLimitCurrency: selected.creditLimitCurrency,
    isaId: selected.isaId,
  };
}

interface PillCustomer {
  name: string;
  erpDisplayName?: string | null;
  emailDomain?: string | null;
  phoneNumber?: string | null;
}

/** The default trigger: the selected customer as a pill. */
function renderCustomerPill(customer: PillCustomer) {
  return (
    <CustomerPill
      customer={{
        name: customer.erpDisplayName ?? customer.name,
        emailDomain: customer.emailDomain ?? undefined,
        phoneNumber: customer.phoneNumber ?? undefined,
      }}
    />
  );
}

/** The list endpoint's own ceiling — past this, searching is the way through. */
const MAX_CUSTOMER_PAGE_SIZE = 100;

/**
 * Customer search with incremental paging: the dropdown pulls a batch at a
 * time as the user scrolls, and a new search starts over from one batch.
 */
function usePagedCustomerSearch(sort: CustomerSort) {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(CUSTOMER_BATCH_SIZE);
  const [searchAtLastReset, setSearchAtLastReset] = useState(search);
  if (search !== searchAtLastReset) {
    // Adjust-state-during-render (no effect), matching the pattern used
    // elsewhere for prop/state syncing.
    setSearchAtLastReset(search);
    setPageSize(CUSTOMER_BATCH_SIZE);
  }
  const { data: customersData, isFetching } = useCustomers({
    search: search || undefined,
    sort,
    pageSize,
  });

  const loadedCount = customersData?.edges.length ?? 0;
  const totalCount = customersData?.pageInfo.totalCount ?? 0;
  const unloaded = totalCount > loadedCount;

  const loadMore = {
    hasMore: unloaded && pageSize < MAX_CUSTOMER_PAGE_SIZE,
    isLoading: isFetching,
    onLoadMore: () =>
      setPageSize((current) =>
        Math.min(current + CUSTOMER_BATCH_SIZE, MAX_CUSTOMER_PAGE_SIZE),
      ),
    // At the endpoint's ceiling there are still matches we can't page to,
    // so say so rather than silently ending the list.
    exhaustedMessage: unloaded ? (
      <T context="customer autocomplete hint when more matches exist than can be listed">
        Keep typing to narrow these results
      </T>
    ) : null,
  };

  return { setSearch, customersData, loadMore };
}

type CustomerSort = 'name' | 'latinFirst' | 'cjkLast';

export function CustomersAutocomplete({
  selectedCustomerId,
  onCustomerSelect,
  allowClear = false,
  className,
  disabled = false,
  sort = 'cjkLast',
  plainTrigger = false,
}: {
  selectedCustomerId: string | undefined;
  onCustomerSelect: (customerId: string | undefined) => void;
  allowClear?: boolean;
  className?: string;
  /** Locks the picker, e.g. while the form around it is submitting. */
  disabled?: boolean;
  /**
   * Show the selected customer as plain text instead of a pill — for inline
   * edits where the field otherwise reads as text.
   */
  plainTrigger?: boolean;
  /**
   * List ordering. Defaults to 'cjkLast' so Latin-named customers lead and
   * CJK/pictographic names sink to the end of the dropdown.
   */
  sort?: CustomerSort;
}) {
  const gt = useGT();
  const { setSearch, customersData, loadMore } = usePagedCustomerSearch(sort);

  // Fetch the selected customer to ensure it's always available for display
  const { data: selectedCustomerData } = useCustomer(selectedCustomerId);
  const selectedCustomer = selectedCustomerData?.customer;

  return (
    <Combobox
      items={customersData?.edges ?? []}
      selectedItems={
        selectedCustomer ? [toCustomerOption(selectedCustomer)] : undefined
      }
      value={selectedCustomerId || null}
      onValueChange={(next) => onCustomerSelect(next ?? '')}
      deselectable
      getValue={(customer) => customer.id}
      getLabel={(customer) => customer.erpDisplayName ?? customer.name}
      // The server matches name, email domain and contact person, so its
      // results are listed as they come (onSearch turns client filtering off).
      onSearch={(query) => setSearch(query)}
      debounceMs={0}
      allowClear={allowClear}
      disabled={disabled}
      loadMore={loadMore}
      className={className}
      contentClassName="w-full"
      renderValue={
        plainTrigger
          ? undefined
          : ({ selected }) => renderCustomerPill(selected[0])
      }
      labels={pickerLabels({
        placeholder: gt('Search customers', {
          context: 'customer search input placeholder',
        }),
        search: gt('Search options...', {
          context: 'search box placeholder inside a picker dropdown',
        }),
      })}
    />
  );
}
